<template>
    <vx-popover hover placement="top" arrow @open="fetchCampaignDetails">
        <font-awesome-icon class="vx-text-slate-500" :icon="faInfoCircle" />

        <template v-slot:content>
            <div v-if="loading">
                <vx-icon-loading spin></vx-icon-loading>
            </div>
            <div v-else class="vx-flex vx-flex-col vx-max-w-xl vx-gap-2">
                <campaign-name
                    class="vx-flex-row-reverse"
                    :modelValue="props.modelValue"
                ></campaign-name>

                <div class="vx-grid vx-grid-cols-5 vx-gap-2">
                    <div class="vx-col-span-1">Created on:</div>
                    <div class="vx-col-span-4">
                        <span class="vx-col-span-4 vx-flex vx-items-center vx-gap-2">
                        <span>{{ fromNow(props.modelValue.created_at)}}</span>
                        <span>-</span>
                        <span>{{ shortDateTime(props.modelValue.created_at)}}</span>
                    </span>
                    </div>

                    <div class="vx-col-span-1">Who:</div>
                    <div class="vx-col-span-4 vx-line-clamp-1" data-test="who">
                        <span v-if="campaign.everyone">Everyone</span>
                        <span v-else-if="segmentNames" :title="segmentNames">{{
                            segmentNames
                        }}</span>
                        <span v-else>-</span>
                    </div>
                    <div class="vx-col-span-1">What:</div>
                    <div class="vx-col-span-4"  data-test="what">
                        <div class="vx-flex vx-flex-col vx-gap-2">
                            <template v-if="firstMessage">
                                <p class="vx-break-words vx-line-clamp-[9]">
                                    {{ firstMessage.body }}
                                </p>
                                <div class="vx-flex vx-items-center vx-gap-2">
                                    <vx-button
                                        size="sm"
                                        color="muted-light"
                                        outline
                                        @click="copyText(firstMessage.body)"
                                    >
                                        Copy Full Text
                                        <font-awesome-icon :icon="faCopy" />
                                    </vx-button>
                                    <vx-button
                                        size="sm"
                                        color="muted"
                                        @click="testing = true"
                                        data-test="send-test"
                                    >
                                        Send Test
                                        <font-awesome-icon
                                            :icon="faFlaskVial"
                                        ></font-awesome-icon>
                                    </vx-button>

                                    <vx-button
                                        v-if="firstMessage.media_url"
                                        :href="firstMessage.media_url"
                                        target="_blank"
                                        size="sm"
                                        color="muted"
                                        data-test="view-attachment"
                                    >
                                        View Attachment
                                        <font-awesome-icon
                                            :icon="faDownload"
                                        ></font-awesome-icon>
                                    </vx-button>

                                    <message-builder-test
                                        v-model:visible="testing"
                                        :bodyMessage="firstMessage.body"
                                        :mediaUrl="firstMessage.media_url"
                                        :mediaContentType="
                                            firstMessage.media_content_type
                                        "
                                    ></message-builder-test>
                                </div>
                            </template>
                            <span v-else>-</span>
                        </div>
                    </div>
                    <div class="vx-col-span-1">When:</div>
                    <span class="vx-col-span-4 vx-flex vx-items-center vx-gap-2">
                        <template v-if="props.modelValue.live_at">
                            <span>{{ fromNow(props.modelValue.live_at)}}</span>
                            <span>-</span>
                            <span>{{ shortDateTime(props.modelValue.live_at)}}</span>
                        </template>
                        <span v-else> - </span>
                    </span>
                </div>
            </div>
        </template>
    </vx-popover>
</template>
<script setup>
import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import {
    faCopy,
    faDownload,
    faFlaskVial,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import MessageBuilderTest from "~/components/general/message-builder/modals/MessageBuilderTest.vue";
import {
    VxButton,
    VxPopover,
    VxIconLoading,
} from "@voxie/frontend-components";
import { fromNow, shortDateTime } from "~/utils/date";
import CampaignName from "./CampaignName.vue";
import { computed, ref } from "vue";
import { useCopyText } from "~/composables/useCopyText";
import campaignsClient from "~/services/campaignsClient";

const props = defineProps({
    groupId: {
        type: String,
        default: undefined,
    },
    modelValue: {
        type: Object,
        required: true,
    },
});

const teamId = Spark.state.currentTeam.id;

const { copyText } = useCopyText();

const campaign = ref(props.modelValue);
const testing = ref(false);
const loading = ref(true);
const loaded = ref(false);

const fetchCampaignDetails = async () => {
    if (loaded.value) {
        loading.value = false;
        return;
    }

    try {
        loading.value = true;
        const response = !props.groupId ?
            await campaignsClient.campaignGet(
                teamId,
                props.modelValue.id
            ) :
            await campaignsClient.groupCampaignGet(
                teamId,
                props.groupId,
                props.modelValue.id
            )

        campaign.value = response?.data;
        loading.value = false;
        loaded.value = true;
    } catch (e) {
        console.error(e);
    }
};

const firstMessage = computed(() => {
    return campaign.value.campaign_messages.length
        ? campaign.value.campaign_messages[0]
        : null;
});

const segmentNames = computed(() => {
    return campaign.value.segments.length
        ? campaign.value.segments.map((segment) => segment.name).join(", ")
        : null;
});

</script>
